import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { useSupportAccessStatus } from '@/queries/supportAccess';

/**
 * For a Lexo Admin viewing another firm that is private: says that its client data is hidden, or,
 * while the firm has given Lexo access, until when and that everything is recorded
 */
export default function PrivateFirmBanner() {
    const { isSuperAdmin, activeLawFirmId, myLawFirmId } = useLawFirmContext();
    const isOtherFirm = isSuperAdmin && !!activeLawFirmId && activeLawFirmId !== myLawFirmId;
    const { data: lawFirm } = useLawFirm(isOtherFirm ? activeLawFirmId : undefined);
    // From the polled status rather than the firm, whose cache can be minutes old
    const { data: status } = useSupportAccessStatus(activeLawFirmId, isOtherFirm);

    if (!isOtherFirm || !status?.isPrivate || !lawFirm) return null;

    const activeGrant = status?.activeGrant;
    const expiresAt =
        activeGrant &&
        DateTime.fromISO(activeGrant.expiresAt).toLocaleString(DateTime.DATETIME_MED);
    const message = expiresAt
        ? `${lawFirm.companyName} has given Lexo access until ${expiresAt}. Everything you open is recorded in the firm's activity log, which its admins can see.`
        : `${lawFirm.companyName} is private. Its matters, contacts and ID verifications are hidden until one of its admins gives Lexo access.`;

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                px: 3,
                py: 1,
                bgcolor: expiresAt ? 'warning.W_50' : 'neutral.N_50',
                borderBottom: '1px solid',
                borderColor: expiresAt ? 'warning.main' : 'neutral.N_100',
            }}
        >
            <LockOutlinedIcon sx={{ fontSize: 18, color: 'neutral.N_700' }} />
            <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                {message}
            </Typography>
        </Stack>
    );
}
